import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import getCroppedImg from '../../utils/getCroppedImg';
import DefaultImage from '../../assets/default-image.png';

// =====================================================
// 🔹 Tipos
// =====================================================

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type DragMode =
  | { type: 'none' }
  | { type: 'draw' }
  | { type: 'move'; startX: number; startY: number; orig: Rect }
  | {
      type: 'resize';
      handle: Handle;
      startX: number;
      startY: number;
      orig: Rect;
    };

type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

interface Props {
  label?: string;
  helpText?: string;

  /** Imagen actual (URL / blob / dataURL) para previsualizar. */
  currentImage?: string;

  /** Si se define, el recorte queda bloqueado a esta relación de aspecto (w/h). */
  aspect?: number;

  /** Previsualiza el recorte como círculo (avatares). El archivo sigue siendo rectangular. */
  round?: boolean;

  /** Formato de salida. `image/png` conserva transparencia (logos). */
  mimeType?: string;

  /** Nombre del archivo resultante. */
  outputName?: string;

  onUpload: (file: File) => void;
  onDelete: () => void;
}

// =====================================================
// 🔹 Constantes
// =====================================================

const MIN_SIZE = 24; // px en pantalla
const HANDLE_HIT = 14; // radio de detección de manijas

// =====================================================
// 🔹 Componente
// =====================================================

const ImageCropField: React.FC<Props> = ({
  label = 'Imagen',
  helpText = 'Arrastra sobre la imagen para marcar el área que quieres conservar.',
  currentImage,
  aspect,
  round = false,
  mimeType = 'image/png',
  outputName = 'image.png',
  onUpload,
  onDelete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  const [src, setSrc] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [hasImage, setHasImage] = useState<boolean>(!!currentImage);

  // Geometría de la imagen mostrada dentro del stage (object-contain)
  const [box, setBox] = useState<Rect | null>(null); // área ocupada por la imagen
  const natRef = useRef({ w: 0, h: 0 });

  const [sel, setSel] = useState<Rect | null>(null);
  const dragRef = useRef<DragMode>({ type: 'none' });
  const dragAnchorRef = useRef({ x: 0, y: 0 });

  // =====================================================
  // 🔹 Selección de archivo
  // =====================================================

  const openPicker = () => inputRef.current?.click();

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setSrc(reader.result as string);
      setSel(null);
      setBox(null);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const startRecrop = () => {
    if (!currentImage) return;
    setSrc(currentImage);
    setSel(null);
    setBox(null);
    setShowCropper(true);
  };

  const cancel = () => {
    setShowCropper(false);
    setSrc(null);
    setSel(null);
    setBox(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = () => {
    onDelete();
    setHasImage(false);
    cancel();
  };

  // =====================================================
  // 🔹 Medición de la imagen dentro del stage
  // =====================================================

  const measure = useCallback(() => {
    const stage = stageRef.current;
    const { w: natW, h: natH } = natRef.current;
    if (!stage || !natW || !natH) return;

    const sw = stage.clientWidth;
    const sh = stage.clientHeight;
    const scale = Math.min(sw / natW, sh / natH);
    const dispW = natW * scale;
    const dispH = natH * scale;

    const nextBox: Rect = {
      x: (sw - dispW) / 2,
      y: (sh - dispH) / 2,
      w: dispW,
      h: dispH,
    };
    setBox(nextBox);

    // Selección inicial: 80% centrado (respetando aspect)
    setSel((prev) => {
      if (prev) return clampToBox(prev, nextBox);
      let w = dispW * 0.8;
      let h = dispH * 0.8;
      if (aspect) {
        if (w / h > aspect) w = h * aspect;
        else h = w / aspect;
      }
      return {
        x: nextBox.x + (dispW - w) / 2,
        y: nextBox.y + (dispH - h) / 2,
        w,
        h,
      };
    });
  }, [aspect]);

  const onImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    natRef.current = {
      w: e.currentTarget.naturalWidth,
      h: e.currentTarget.naturalHeight,
    };
    measure();
  };

  useLayoutEffect(() => {
    if (!showCropper) return;
    measure();
    const ro = new ResizeObserver(() => measure());
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [showCropper, measure]);

  // =====================================================
  // 🔹 Interacción (pointer = mouse + touch + pen)
  // =====================================================

  const hitHandle = (px: number, py: number, r: Rect): Handle | null => {
    const pts: Record<Handle, [number, number]> = {
      nw: [r.x, r.y],
      n: [r.x + r.w / 2, r.y],
      ne: [r.x + r.w, r.y],
      e: [r.x + r.w, r.y + r.h / 2],
      se: [r.x + r.w, r.y + r.h],
      s: [r.x + r.w / 2, r.y + r.h],
      sw: [r.x, r.y + r.h],
      w: [r.x, r.y + r.h / 2],
    };
    for (const key of Object.keys(pts) as Handle[]) {
      const [hx, hy] = pts[key];
      if (Math.abs(px - hx) <= HANDLE_HIT && Math.abs(py - hy) <= HANDLE_HIT) {
        return key;
      }
    }
    return null;
  };

  const localPoint = (e: React.PointerEvent) => {
    const rect = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!box) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    const { x, y } = localPoint(e);

    if (sel) {
      const handle = hitHandle(x, y, sel);
      if (handle) {
        dragRef.current = {
          type: 'resize',
          handle,
          startX: x,
          startY: y,
          orig: sel,
        };
        return;
      }
      if (x >= sel.x && x <= sel.x + sel.w && y >= sel.y && y <= sel.y + sel.h) {
        dragRef.current = { type: 'move', startX: x, startY: y, orig: sel };
        return;
      }
    }

    // Nuevo recuadro
    const cx = clamp(x, box.x, box.x + box.w);
    const cy = clamp(y, box.y, box.y + box.h);
    dragRef.current = { type: 'draw' };
    setSel({ x: cx, y: cy, w: 0, h: 0 });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (drag.type === 'none' || !box) return;

    const { x, y } = localPoint(e);
    const px = clamp(x, box.x, box.x + box.w);
    const py = clamp(y, box.y, box.y + box.h);

    if (drag.type === 'draw') {
      const anchor = dragAnchorRef.current;
      let nx = Math.min(anchor.x, px);
      let ny = Math.min(anchor.y, py);
      let nw = Math.abs(px - anchor.x);
      let nh = Math.abs(py - anchor.y);
      if (aspect) {
        // Ajusta a la relación de aspecto sin salir de la caja
        if (nw / nh > aspect) nw = nh * aspect;
        else nh = nw / aspect;
        if (px < anchor.x) nx = anchor.x - nw;
        if (py < anchor.y) ny = anchor.y - nh;
      }
      setSel(clampToBox({ x: nx, y: ny, w: nw, h: nh }, box));
      return;
    }

    if (drag.type === 'move') {
      const dx = x - drag.startX;
      const dy = y - drag.startY;
      let nx = drag.orig.x + dx;
      let ny = drag.orig.y + dy;
      nx = clamp(nx, box.x, box.x + box.w - drag.orig.w);
      ny = clamp(ny, box.y, box.y + box.h - drag.orig.h);
      setSel({ ...drag.orig, x: nx, y: ny });
      return;
    }

    // resize
    let { x: rx, y: ry, w: rw, h: rh } = drag.orig;
    const right = rx + rw;
    const bottom = ry + rh;
    const h = drag.handle;

    if (h.includes('w')) {
      rx = clamp(px, box.x, right - MIN_SIZE);
      rw = right - rx;
    }
    if (h.includes('e')) {
      rw = clamp(px, rx + MIN_SIZE, box.x + box.w) - rx;
    }
    if (h.includes('n')) {
      ry = clamp(py, box.y, bottom - MIN_SIZE);
      rh = bottom - ry;
    }
    if (h.includes('s')) {
      rh = clamp(py, ry + MIN_SIZE, box.y + box.h) - ry;
    }

    let next: Rect = { x: rx, y: ry, w: rw, h: rh };

    if (aspect) {
      // Mantiene la relación de aspecto anclando al lado opuesto
      if (h === 'e' || h === 'w') {
        const newH = next.w / aspect;
        next.y = clamp(
          next.y + (next.h - newH) / 2,
          box.y,
          box.y + box.h - newH
        );
        next.h = newH;
      } else {
        const newW = next.h * aspect;
        if (h.includes('w')) next.x = right - newW;
        next.w = newW;
      }
      next = clampToBox(next, box);
    }

    setSel(next);
  };

  const onPointerDownCapture = (e: React.PointerEvent) => {
    // Guarda el ancla para el modo "draw"
    if (stageRef.current) {
      const rect = stageRef.current.getBoundingClientRect();
      dragAnchorRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    try {
      (e.target as Element).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    // Descarta selecciones accidentales muy pequeñas
    if (sel && (sel.w < MIN_SIZE || sel.h < MIN_SIZE) && box) {
      setSel(null);
    }
    dragRef.current = { type: 'none' };
  };

  // =====================================================
  // 🔹 Aplicar recorte
  // =====================================================

  const apply = async () => {
    if (!src || !sel || !box) return;
    const { w: natW, h: natH } = natRef.current;
    const scale = natW / box.w; // px naturales por px pantalla

    const crop = {
      x: clamp((sel.x - box.x) * scale, 0, natW),
      y: clamp((sel.y - box.y) * scale, 0, natH),
      width: clamp(sel.w * scale, 1, natW),
      height: clamp(sel.h * scale, 1, natH),
    };

    try {
      const blob = await getCroppedImg(src, crop, mimeType);
      const file = new File([blob], outputName, { type: mimeType });
      onUpload(file);
      setHasImage(true);
      cancel();
    } catch (err) {
      console.error('No se pudo recortar la imagen', err);
    }
  };

  const applyFull = async () => {
    // Usa la imagen completa sin recortar
    if (!src) return;
    const { w: natW, h: natH } = natRef.current;
    try {
      const blob = await getCroppedImg(
        src,
        { x: 0, y: 0, width: natW, height: natH },
        mimeType
      );
      const file = new File([blob], outputName, { type: mimeType });
      onUpload(file);
      setHasImage(true);
      cancel();
    } catch (err) {
      console.error('No se pudo procesar la imagen', err);
    }
  };

  useEffect(() => {
    setHasImage(!!currentImage);
  }, [currentImage]);

  // =====================================================
  // 🔹 Render
  // =====================================================

  return (
    <div className="w-full">
      {label && (
        <label className="mb-2 block text-sm font-medium text-black dark:text-white">
          {label}
        </label>
      )}

      {/* Preview + acciones */}
      {!showCropper && (
        <div className="flex flex-col items-center gap-3">
          <div
            className={`relative h-32 w-32 overflow-hidden border border-stroke bg-white dark:border-strokedark dark:bg-boxdark ${
              round ? 'rounded-full' : 'rounded-md'
            }`}
          >
            <img
              src={currentImage || DefaultImage}
              alt="Vista previa"
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={openPicker}
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90"
            >
              {hasImage ? 'Cambiar imagen' : 'Subir imagen'}
            </button>

            {hasImage && currentImage && (
              <button
                type="button"
                onClick={startRecrop}
                className="rounded border border-stroke bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-2 dark:border-strokedark dark:bg-boxdark dark:text-white"
              >
                Recortar
              </button>
            )}

            {hasImage && (
              <button
                type="button"
                onClick={remove}
                className="rounded bg-meta-1 px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90"
              >
                Eliminar
              </button>
            )}
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png, image/jpeg, image/webp"
        onChange={handleFile}
        className="hidden"
      />

      {/* Recortador */}
      {showCropper && src && (
        <div className="mt-2 rounded-lg border border-stroke p-3 dark:border-strokedark">
          <p className="mb-2 text-center text-xs text-body dark:text-bodydark">
            {helpText}
          </p>

          <div
            ref={stageRef}
            className="relative mx-auto h-72 w-full max-w-md touch-none select-none overflow-hidden rounded-md bg-[#0f172a]"
            onPointerDownCapture={onPointerDownCapture}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <img
              src={src}
              alt="Imagen a recortar"
              onLoad={onImgLoad}
              draggable={false}
              className="pointer-events-none absolute inset-0 h-full w-full object-contain"
            />

            {sel && box && (
              <div
                className={`absolute border border-white ${
                  round ? 'rounded-full' : ''
                }`}
                style={{
                  left: sel.x,
                  top: sel.y,
                  width: sel.w,
                  height: sel.h,
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                }}
              >
                {/* tercios */}
                <span className="pointer-events-none absolute left-1/3 top-0 h-full w-px bg-white/40" />
                <span className="pointer-events-none absolute left-2/3 top-0 h-full w-px bg-white/40" />
                <span className="pointer-events-none absolute left-0 top-1/3 h-px w-full bg-white/40" />
                <span className="pointer-events-none absolute left-0 top-2/3 h-px w-full bg-white/40" />

                {/* manijas */}
                {(
                  ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as Handle[]
                ).map((hkey) => (
                  <span
                    key={hkey}
                    className="absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-sm border border-primary bg-white"
                    style={handleStyle(hkey)}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="mt-3 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={cancel}
              className="rounded border border-stroke bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-2 dark:border-strokedark dark:bg-boxdark dark:text-white"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={applyFull}
              className="rounded border border-stroke bg-white px-4 py-2 text-sm font-medium text-black hover:bg-gray-2 dark:border-strokedark dark:bg-boxdark dark:text-white"
            >
              Usar imagen completa
            </button>

            <button
              type="button"
              onClick={apply}
              disabled={!sel || sel.w < MIN_SIZE || sel.h < MIN_SIZE}
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Aplicar recorte
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================
// 🔹 Helpers
// =====================================================

function clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}

function clampToBox(r: Rect, box: Rect): Rect {
  const w = Math.min(r.w, box.w);
  const h = Math.min(r.h, box.h);
  const x = clamp(r.x, box.x, box.x + box.w - w);
  const y = clamp(r.y, box.y, box.y + box.h - h);
  return { x, y, w, h };
}

function handleStyle(h: Handle): React.CSSProperties {
  const map: Record<Handle, React.CSSProperties> = {
    nw: { left: 0, top: 0, cursor: 'nwse-resize' },
    n: { left: '50%', top: 0, cursor: 'ns-resize' },
    ne: { left: '100%', top: 0, cursor: 'nesw-resize' },
    e: { left: '100%', top: '50%', cursor: 'ew-resize' },
    se: { left: '100%', top: '100%', cursor: 'nwse-resize' },
    s: { left: '50%', top: '100%', cursor: 'ns-resize' },
    sw: { left: 0, top: '100%', cursor: 'nesw-resize' },
    w: { left: 0, top: '50%', cursor: 'ew-resize' },
  };
  return map[h];
}

export default ImageCropField;
